<script setup lang="ts">
    import { ref, computed, watch } from 'vue'
    import { EditorState } from '@common/Common'

    import type { SurveyEditorDialogViewModel } from '@Evaluation/AlternativesSurveys/Components/SurveyEditorDialog'

    /* ---------------- props / emits ---------------- */

    const props = defineProps<{
        viewModel: SurveyEditorDialogViewModel
    }>()

    const emit = defineEmits<{
        (e: 'create-survey', surveyId: number | null, name: string | null, description: string | null): void
        (e: 'update-survey', surveyId: number | null, name: string | null, description: string | null): void
        (e: 'delete-survey', surveyId: number | null): void
    }>()

    /* ---------------- shortcuts ---------------- */

    const vm = computed(() => props.viewModel)
    const opened = computed(() => vm.value?.opened ?? false)

    /* ---------------- derived ---------------- */

    const state = computed(() => vm.value.editorState)
    const title = computed(() => vm.value.editorTitle)
    const nameError = computed(() => vm.value.nameError)

    /* ---------------- local state ---------------- */

    const Initialized = ref(false)

    const SurveyId = ref<number | null>(null)
    const Name = ref<string | null>(null)
    const Description = ref<string | null>(null)

    /* ---------------- watchers ---------------- */

    watch(state, (val) =>
    {
        if (val === EditorState.Closed)
        {
            Initialized.value = false
            return
        }

        vm.value.clearValidationErrors()

        SurveyId.value = vm.value.surveyId
        Name.value = vm.value.surveyName
        Description.value = vm.value.surveyDescription

        Initialized.value = true
    })

    /* ---------------- actions ---------------- */

    function CreateCommand()
    {
        vm.value.clearValidationErrors()
        emit('create-survey', SurveyId.value, Name.value, Description.value)
    }

    function UpdateCommand()
    {
        vm.value.clearValidationErrors()
        emit('update-survey', SurveyId.value, Name.value, Description.value)
    }

    function DeleteCommand()
    {
        emit('delete-survey', SurveyId.value)
    }

    function close()
    {
        vm.value.close()
    }
</script>


<template>
    <div class="modal-mask" v-if="opened">
        <div class="modal-wrapper">
            <div class="modal-dialog">
                <div class="modal-content">
                    <div class="modal-header">
                        <h5 class="modal-title">{{title}}</h5>
                        <button @click="close" type="button" class="close">&times;</button>
                    </div>

                    <div class="modal-body">
                        <h2 v-if="Initialized === false">
                            <i class="fa fa-spinner fa-spin"></i> Loading...
                        </h2>
                        <div v-if="Initialized">
                            <div class="form-group">
                                <label>Name</label>
                                <input type="text" v-model.trim="Name" class="form-control">
                                <div class="error">{{nameError}}</div>
                            </div>
                            <div class="form-group">
                                <label>Description</label>
                                <textarea type="text" v-model.trim="Description" class="form-control"></textarea>
                            </div>
                        </div>
                    </div>

                    <div class="modal-footer justify-content-between">

                        <div class="dropdown dropup">
                            <button v-if="state === 'Editor'" type="button" class="btn btn-danger" data-toggle="dropdown">Delete</button>
                            <div class="dropdown-menu" role="menu" style="padding: 10px;">
                                <a class="dropdown-item">Cancel</a>
                                <a class="dropdown-item" @click="DeleteCommand">Confirm</a>
                            </div>
                        </div>

                        <div>
                            <button v-if="state === 'Creator'" @click="CreateCommand" type="button" class="btn btn-primary">Create</button>
                            <button v-if="state === 'Editor'" @click="UpdateCommand" type="button" class="btn btn-primary">Save</button>
                            <button @click="close" type="button" class="btn btn-outline-secondary">Cancel</button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>
